import { useState } from 'react';
import { useField, useFormApi } from 'informed';
import { v4 as uuidv4 } from 'uuid';
import { useReduxState } from 'hooks';
import { Button, SortableList } from 'components/common';
import { Text } from '.';

interface ArrayElementProps extends Props {
  id: string;
  onRemove: (id: string) => void;
}

const ArrayElement = ({
  field,
  hideActions,
  id,
  inputElement,
  onRemove
}: ArrayElementProps) => {
  const { messages, theme } = useReduxState();

  const InputEl = inputElement || Text;

  return (
    <div className="item">
      <InputEl disabled={hideActions} field={field} />

      {hideActions ? null : (
        <Button
          aria-label={messages.commonDelete}
          category="light"
          data-tooltip={messages.commonDelete}
          icon="trash-alt"
          onClick={() => onRemove(id)}
          size="small"
          style={{ marginLeft: 10 }}
        />
      )}

      <style jsx>{`
        .item {
          align-items: center;
          background-color: white;
          border: 2px solid transparent;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;
          padding: 3px 0;
        }
      `}</style>
    </div>
  );
};

interface Props {
  field: string;
  hideActions?: boolean;
  inputElement?: React.FunctionComponent<any>;
}

const SortableArrayList = (props: Props) => {
  const { fieldApi, fieldState, render, userProps } = useField<unknown[]>({
    ...props
  });

  const { hideActions = false } = userProps;

  const { messages } = useReduxState();

  const formApi = useFormApi();

  const [itemIds, setItemIds] = useState<string[]>(() =>
    (fieldState?.value || []).map(() => uuidv4())
  );

  /**
   * Informed doesn't mark the field as changed when items are added, removed or sorted.
   * This helper reads the current values from the form and writes them back unchanged
   * in order to mark the field as changed without modifying the data.
   */
  const markFieldAsChanged = () => {
    fieldApi.setValue(
      itemIds.map((_, index) => formApi.getValue(`${props.field}[${index}]`))
    );
  };

  const handleAddItem = () => {
    markFieldAsChanged();
    setItemIds([...itemIds, uuidv4()]);
  };

  const handleRemoveItem = (id: string) => {
    markFieldAsChanged();
    setItemIds(itemIds.filter((itemId) => itemId !== id));
  };

  const handleSort = (sortedItemIds: string[]) => {
    markFieldAsChanged();
    setItemIds(sortedItemIds);
  };

  return render(
    <>
      <SortableList
        items={itemIds}
        itemStyles={{ padding: 0 }}
        listId={`${props.field}-sortable-array-list`}
        moveWithHandle={true}
        onChange={handleSort}
        renderItem={(id, index) => (
          <ArrayElement
            field={`${props.field}[${index}]`}
            hideActions={hideActions}
            id={id}
            inputElement={props.inputElement}
            onRemove={handleRemoveItem}
          />
        )}
        syncWithParent={true}
      />

      {hideActions ? null : (
        <Button
          category="light"
          icon="plus"
          onClick={handleAddItem}
          size="small"
          style={{ marginTop: 5 }}
          text={messages.commonAddOption}
        />
      )}
    </>
  );
};

export default SortableArrayList;
